import { z } from 'zod';

import { QUESTION_TYPE } from 'types/question.types';
import { QuestionResponse, TypedQuestion } from 'types/question-response.types';

import * as long from './questions/long';
import * as radio from './questions/radio';
import * as check from './questions/check';
import * as scale from './questions/scale';

/**
 * @example
 * ```ts
 *  {
 *    type: QUESTION_TYPE;
 *    req: 'required' | 'optional';
 *    value?: string | string[];
 *    otherValue?: string
 *  }
 *  ```
 * @remarks
 * - `value` is a string for `LONG` and `SCALE` questions
 * - `value` is a string[] for `CHECK` questions
 * - `otherValue` is only defined for `CHECK` and `RADIO` questions with `hasOtherOption` === True
 */
export type QuestionResponseFormValue =
  | long.FormValue
  | radio.FormValue
  | check.FormValue
  | scale.FormValue;

export const responseFormValue = (
  formValue: QuestionResponseFormValue
): string | string[] | undefined => {
  switch (formValue.type) {
    case QUESTION_TYPE.LONG:
      return formValue.value;
    case QUESTION_TYPE.RADIO:
      return radio.valueFromFormValue(formValue);
    case QUESTION_TYPE.CHECK:
      return check.valueFromFormValue(formValue);
    case QUESTION_TYPE.LINEAR_SCALE:
    case QUESTION_TYPE.NPS:
    case QUESTION_TYPE.SATISFACTION:
    case QUESTION_TYPE.CONNECTION:
    case QUESTION_TYPE.RETENTION:
      return formValue.value;
    default:
      console.error('unimplemented type');
      return undefined;
  }
};

export const schematizeQuestion = (
  question: TypedQuestion
): z.ZodType<QuestionResponseFormValue> | undefined => {
  switch (question.type) {
    case QUESTION_TYPE.LONG:
      return long.validateAndSchematize(question);
    case QUESTION_TYPE.RADIO:
      return radio.validateAndSchematize(question);
    case QUESTION_TYPE.CHECK:
      return check.validateAndSchematize(question);
    case QUESTION_TYPE.LINEAR_SCALE:
    case QUESTION_TYPE.NPS:
    case QUESTION_TYPE.SATISFACTION:
    case QUESTION_TYPE.CONNECTION:
    case QUESTION_TYPE.RETENTION:
      return scale.validateAndSchematize(question);
    default:
      console.error('unimplemented type');
      return undefined;
  }
};

export const initializeQuestion = (
  question: TypedQuestion,
  response?: QuestionResponse
): QuestionResponseFormValue | undefined => {
  switch (question.type) {
    case QUESTION_TYPE.LONG:
      return long.validateAndInitialize(question, response);
    case QUESTION_TYPE.RADIO:
      return radio.validateAndInitialize(question, response);
    case QUESTION_TYPE.CHECK:
      return check.validateAndInitialize(question, response);
    case QUESTION_TYPE.LINEAR_SCALE:
    case QUESTION_TYPE.NPS:
    case QUESTION_TYPE.SATISFACTION:
    case QUESTION_TYPE.CONNECTION:
    case QUESTION_TYPE.RETENTION:
      return scale.validateAndInitialize(question, response);
    default:
      return undefined;
  }
};

export const serializeResponse = (
  id: number,
  response: QuestionResponseFormValue
): QuestionResponse | undefined => {
  switch (response.type) {
    case QUESTION_TYPE.LONG:
      return long.validateAndSerialize(id, response);
    case QUESTION_TYPE.RADIO:
      return radio.validateAndSerialize(id, response);
    case QUESTION_TYPE.CHECK:
      return check.validateAndSerialize(id, response);
    case QUESTION_TYPE.LINEAR_SCALE:
    case QUESTION_TYPE.NPS:
    case QUESTION_TYPE.SATISFACTION:
    case QUESTION_TYPE.CONNECTION:
    case QUESTION_TYPE.RETENTION:
      return scale.validateAndSerialize(id, response);
    default:
      return undefined;
  }
};
